import { useState } from 'react';
import {
  NetcashBatchClient,
  NetcashBatchDTO,
  AddInvoicesToNetcashBatchRequestDTO,
  ApiResponse_netcashBatchDTO,
  ApiResponse_netcashAddInvoicesResultDTO,
  ApiResponse_netcashInvoiceAvailabilityResultDTO,
  NetcashInvoiceAvailabilityRequestDTO,
  AddInvoiceBatchToNetcashBatchRequestDTO,
  CreateNetcashBatchRequestDTO,
  PushNetcashBatchRequestDTO,
  ApiResponse_string,
} from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';

export const useNetcashBatchApi = () => {
  const apiClient = new NetcashBatchClient(API_BASE_URL, axiosInstance);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // NSwag's generic dedup mistypes list responses' `data` as ActivityDTO[] (name collision with
  // another List<T> wrapper) - the actual runtime shape is NetcashBatchDTO[], so cast explicitly.
  const getDrafts = async (): Promise<{ success?: boolean; message?: string; data?: NetcashBatchDTO[] }> => {
    try {
      return (await apiClient.getNetcashBatchDrafts()) as unknown as { success?: boolean; message?: string; data?: NetcashBatchDTO[] };
    } catch (err: any) {
      setError(err.message || 'Error fetching draft Netcash batches');
      throw err;
    }
  };

  const createDraft = async (year: number, month: number): Promise<ApiResponse_netcashBatchDTO> => {
    setIsLoading(true);
    try {
      const body: CreateNetcashBatchRequestDTO = { year, month };
      return await apiClient.createNetcashBatchDraft(body);
    } catch (err: any) {
      setError(err.message || 'Error creating draft Netcash batch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getHistory = async (): Promise<{ success?: boolean; message?: string; data?: NetcashBatchDTO[] }> => {
    setIsLoading(true);
    try {
      return (await apiClient.getNetcashBatchHistory()) as unknown as { success?: boolean; message?: string; data?: NetcashBatchDTO[] };
    } catch (err: any) {
      setError(err.message || 'Error fetching Netcash batch history');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getById = async (netcashBatchId: string): Promise<ApiResponse_netcashBatchDTO> => {
    try {
      return await apiClient.getNetcashBatch(netcashBatchId);
    } catch (err: any) {
      setError(err.message || 'Error fetching Netcash batch');
      throw err;
    }
  };

  // Which of these invoices are already committed to a Netcash batch (draft, or sent and not yet collected)
  const getInvoiceAvailability = async (invoiceIds: string[]): Promise<ApiResponse_netcashInvoiceAvailabilityResultDTO> => {
    try {
      const body: NetcashInvoiceAvailabilityRequestDTO = { invoiceIds };
      return await apiClient.getNetcashInvoiceAvailability(body);
    } catch (err: any) {
      setError(err.message || 'Error checking Netcash invoice availability');
      throw err;
    }
  };

  const addInvoices = async (netcashBatchId: string, invoiceIds: string[]): Promise<ApiResponse_netcashAddInvoicesResultDTO> => {
    setIsLoading(true);
    try {
      const body: AddInvoicesToNetcashBatchRequestDTO = { invoiceIds };
      return await apiClient.addInvoicesToNetcashBatch(netcashBatchId, body);
    } catch (err: any) {
      setError(err.message || 'Error adding invoices to Netcash batch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Adds every outstanding debit order invoice in the invoice batch to the given draft, or to a
  // NEW draft for year/month when no netcashBatchId is given.
  const addFromInvoiceBatch = async (
    invoiceBatchId: string,
    target: { netcashBatchId?: string; year?: number; month?: number }
  ): Promise<ApiResponse_netcashAddInvoicesResultDTO> => {
    setIsLoading(true);
    try {
      const body: AddInvoiceBatchToNetcashBatchRequestDTO = {
        netcashBatchId: target.netcashBatchId,
        year: target.year,
        month: target.month,
      };
      return await apiClient.addDebitOrderInvoiceBatchToNetcashBatch(invoiceBatchId, body);
    } catch (err: any) {
      setError(err.message || 'Error adding invoice batch to Netcash batch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const removeLineItem = async (lineItemId: string): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.removeNetcashBatchLineItem(lineItemId);
    } catch (err: any) {
      setError(err.message || 'Error removing line item');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Queues the batch for a background push and returns immediately - the batch comes back
  // with isPushing=true, not the final push result. actionDate is a plain yyyy-MM-dd string.
  const push = async (netcashBatchId: string, actionDate: string): Promise<ApiResponse_netcashBatchDTO> => {
    setIsLoading(true);
    try {
      const body: PushNetcashBatchRequestDTO = { actionDate };
      return await apiClient.pushNetcashBatch(netcashBatchId, body);
    } catch (err: any) {
      setError(err.message || 'Error queuing Netcash batch for push');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDraft = async (netcashBatchId: string): Promise<ApiResponse_string> => {
    setIsLoading(true);
    try {
      return await apiClient.deleteNetcashBatchDraft(netcashBatchId);
    } catch (err: any) {
      setError(err.message || 'Error deleting draft Netcash batch');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // The batch was cancelled on Netcash - frees its sent members so they can go in a new batch.
  const markCancelled = async (netcashBatchId: string): Promise<ApiResponse_netcashBatchDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.markNetcashBatchCancelled(netcashBatchId);
    } catch (err: any) {
      setError(err.message || 'Error marking Netcash batch as cancelled');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const releaseLineItem = async (lineItemId: string): Promise<ApiResponse_netcashBatchDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.releaseNetcashBatchLineItem(lineItemId);
    } catch (err: any) {
      setError(err.message || 'Error releasing member');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    getDrafts, createDraft, getInvoiceAvailability, getHistory, getById, addInvoices, addFromInvoiceBatch, removeLineItem, push,
    deleteDraft, markCancelled, releaseLineItem, isLoading, error,
  };
};
